import Container from "@/components/Container/Container.tsx";
import { HolderForm } from "@/components/HolderForm/HolderForm.tsx";
import { PaymentForm } from "@/components/Payment/PaymentForm/PaymentForm.tsx";
import { ReservationSidebar } from "@/components/ReservationSidebar/ReservationSidebar.tsx";
import { Stepper } from "@/components/Stepper/Stepper.tsx";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { useScrollToTop } from "@/helpers/scrollToTop.ts";
import { PaymentProviderEnum, PaymentType } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useIsFormValid } from "@/pages/BookingEngine/hooks/useIsFormValid.ts";
import { ReservationHolderInformationProps } from "@/pages/ReservationHolderInformation/ReservationHolderInformationProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

export const HolderAndPaymentInfo = ({
  holderFormData,
  paymentFormData,
}: ReservationHolderInformationProps) => {
  const scrollToTop = useScrollToTop();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const { paymentEnabled, paymentType } = useGetOrganization();
  const { setPage, setReservationHolderData, calculatedCartData, setPaymentData } = useEngineStore(
    useShallow((state) => ({
      setPage: state.setPage,
      setReservationHolderData: state.setReservationHolderData,
      calculatedCartData: state.calculatedCartData,
      setPaymentData: state.setPaymentData,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);

  const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;

  const shouldShowPaymentForm = useMemo(
    () =>
      !isForcedInquiry && paymentEnabled && paymentType === PaymentProviderEnum.WsPayPer && isCardGuarantee,
    [isForcedInquiry, paymentEnabled, paymentType, isCardGuarantee],
  );

  const { isFullFormValid, isHolderFormValid } = useIsFormValid(holderFormData, paymentFormData);

  const { register, handleSubmit, errors, setValue, watch, trigger, reset, setFocus } = holderFormData;
  const { handleSubmitPaymentForm, onSubmitPaymentForm } = paymentFormData;

  const showResetButton = false;

  const handleContinue = async () => {
    await handleSubmit((data) => setReservationHolderData(data))();
    if (!isForcedInquiry && paymentEnabled && isCardGuarantee) {
      await handleSubmitPaymentForm(onSubmitPaymentForm)();
    } else {
      setPaymentData(undefined);
    }
    setPage(AppPage.ReservationOverview);
    handleCloseCartDrawer();
    scrollToTop();
  };

  return (
    <div>
      <Stepper disableNext={shouldShowPaymentForm ? !isFullFormValid : !isHolderFormValid} />
      <main className="holderWrap o-page">
        <div className="o-page__inner">
          <section className="c-holder-information-options-wrapper">
            <Container>
              <div className="holderOptions c-holder-information-options">
                <div className="c-holder-information-options__wrap">
                  <div className="c-holder-information-options__options">
                    <HolderForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      trigger={trigger}
                      setFocus={setFocus}
                    />
                    {showResetButton && (
                      <button
                        className="c-be-button c-be-button--secondary c-be-button--small"
                        onClick={() => reset()}
                      >
                        <span className="c-be-button__label">Reset</span>
                      </button>
                    )}
                    {shouldShowPaymentForm && (
                      <div className="c-holder-information-options__payment">
                        <PaymentForm paymentFormData={paymentFormData} holderFormData={holderFormData} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="c-holder-information-options__summary">
                  <div className="c-holder-information-options__summary-inner">
                    <ReservationSidebar
                      hideRemoveRate
                      continueDisabled={shouldShowPaymentForm ? !isFullFormValid : !isHolderFormValid}
                      onContinue={handleContinue}
                    />
                  </div>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </main>
    </div>
  );
};
